import React, { useState } from "react";

import {
    Button,
    Card,
    Space
} from "antd";

import {
    PlusOutlined,
    ReloadOutlined,
    FileExcelOutlined,
    FilePdfOutlined
} from "@ant-design/icons";

import * as XLSX from "xlsx";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import usePreorders from "../hooks/usePreorders";
import PreordersTable from "./PreordersTable";
import NewOrderModal from "./NewOrderModal";

export default function KermesPedidos({ userId }) {

    const {
        orders,
        loading,
        reload
    } = usePreorders();

    const [modalOpen, setModalOpen] = useState(false);


    const handleCreated = () => {

        setModalOpen(false);

        reload();

    };


    // =====================================================
    // EXCEL
    // =====================================================

    const exportExcel = () => {

        if (!orders.length) {
            return;
        }

        const rows = [];

        orders.forEach(order => {

            /*
             * Una fila por producto.
             *
             * De esta manera el Excel queda realmente
             * desglosado y después puedes filtrarlo.
             */

            const items = order.items || [];

            if (items.length === 0) {

                rows.push({
                    Pedido: order.id,
                    Cliente: order.customer_name,
                    Telefono: order.customer_phone || "",
                    Entrega: order.delivery_date || "",
                    Producto: "",
                    Cantidad: 0,
                    Total: Number(order.total || 0),
                    Anticipo: Number(order.deposit || 0),
                    Restante: Number(order.remaining || 0),
                    Estado: order.status
                });

                return;
            }


            items.forEach(item => {

                rows.push({

                    Pedido: order.id,

                    Cliente:
                        order.customer_name,

                    Telefono:
                        order.customer_phone || "",

                    Entrega:
                        order.delivery_date || "",

                    Producto:
                        item.product_name || "",

                    Cantidad:
                        Number(item.quantity || 0),

                    Total:
                        Number(order.total || 0),

                    Anticipo:
                        Number(order.deposit || 0),

                    Restante:
                        Number(order.remaining || 0),

                    Estado:
                        order.status

                });

            });

        });


        const worksheet =
            XLSX.utils.json_to_sheet(rows);


        worksheet["!cols"] = [

            { wch: 10 }, // Pedido
            { wch: 25 }, // Cliente
            { wch: 18 }, // Teléfono
            { wch: 15 }, // Entrega
            { wch: 25 }, // Producto
            { wch: 12 }, // Cantidad
            { wch: 15 }, // Total
            { wch: 15 }, // Anticipo
            { wch: 15 }, // Restante
            { wch: 15 }  // Estado

        ];


        const workbook =
            XLSX.utils.book_new();


        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Pedidos"
        );


        const fecha =
            new Date()
                .toISOString()
                .slice(0, 10);


        XLSX.writeFile(
            workbook,
            `pedidos-kermes-${fecha}.xlsx`
        );

    };


    // =====================================================
    // PDF
    // =====================================================

    const exportPDF = () => {

        if (!orders.length) {
            return;
        }


        const doc = new jsPDF({
            orientation: "landscape"
        });


        const fecha = new Date();


        const fechaTexto =
            fecha.toLocaleString(
                "es-MX",
                {
                    dateStyle: "long",
                    timeStyle: "short"
                }
            );


        // =============================
        // TÍTULO
        // =============================

        doc.setFontSize(18);

        doc.text(
            "PEDIDOS ANTICIPADOS - KERMÉS",
            14,
            18
        );


        doc.setFontSize(9);

        doc.text(
            `Generado: ${fechaTexto}`,
            14,
            25
        );


        // =============================
        // RESUMEN
        // =============================

        const totalPedidos =
            orders.length;


        const pendientes =
            orders.filter(
                order =>
                    order.status === "pending"
            ).length;


        const completados =
            orders.filter(
                order =>
                    order.status === "completed"
            ).length;


        const cancelados =
            orders.filter(
                order =>
                    order.status === "cancelled"
            ).length;


        const totalImporte =
            orders
                .filter(
                    order =>
                        order.status !== "cancelled"
                )
                .reduce(
                    (sum, order) =>
                        sum +
                        Number(order.total || 0),
                    0
                );


        const totalAnticipos =
            orders
                .filter(
                    order =>
                        order.status !== "cancelled"
                )
                .reduce(
                    (sum, order) =>
                        sum +
                        Number(order.deposit || 0),
                    0
                );


        const totalRestante =
            orders
                .filter(
                    order =>
                        order.status === "pending"
                )
                .reduce(
                    (sum, order) =>
                        sum +
                        Number(order.remaining || 0),
                    0
                );


        autoTable(doc, {

            startY: 31,

            head: [[
                "Pedidos",
                "Pendientes",
                "Completados",
                "Cancelados",
                "Importe",
                "Anticipos",
                "Por cobrar"
            ]],

            body: [[
                totalPedidos,
                pendientes,
                completados,
                cancelados,
                `$${totalImporte.toFixed(2)}`,
                `$${totalAnticipos.toFixed(2)}`,
                `$${totalRestante.toFixed(2)}`
            ]],

            theme: "grid",

            styles: {
                fontSize: 9
            }

        });


        // =============================
        // PEDIDOS
        // =============================

        const body = [];


        orders.forEach(order => {

            const items =
                order.items || [];


            const productos =
                items
                    .map(item =>
                        `${item.product_name || "Producto"} x${item.quantity}`
                    )
                    .join(", ");


            body.push([

                order.id,

                order.customer_name,

                order.customer_phone || "-",

                order.delivery_date || "-",

                productos || "-",

                order.total_items,

                `$${Number(
                    order.total || 0
                ).toFixed(2)}`,

                `$${Number(
                    order.deposit || 0
                ).toFixed(2)}`,

                `$${Number(
                    order.remaining || 0
                ).toFixed(2)}`,

                order.status

            ]);

        });


        autoTable(doc, {

            startY:
                doc.lastAutoTable.finalY + 10,

            head: [[
                "#",
                "Cliente",
                "Teléfono",
                "Entrega",
                "Productos",
                "Pzas.",
                "Total",
                "Anticipo",
                "Restante",
                "Estado"
            ]],

            body,

            theme: "grid",

            styles: {
                fontSize: 7,
                cellPadding: 2
            },

            columnStyles: {

                0: { cellWidth: 10 },

                1: { cellWidth: 30 },

                2: { cellWidth: 24 },

                3: { cellWidth: 22 },

                4: { cellWidth: 55 },

                5: { cellWidth: 12 },

                6: { cellWidth: 20 },

                7: { cellWidth: 20 },

                8: { cellWidth: 20 },

                9: { cellWidth: 20 }

            }

        });


        // =============================
        // PIE DE PÁGINA
        // =============================

        const pageCount =
            doc.internal.getNumberOfPages();


        for (
            let i = 1;
            i <= pageCount;
            i++
        ) {

            doc.setPage(i);

            doc.setFontSize(8);

            doc.text(
                `Página ${i} de ${pageCount}`,
                14,
                doc.internal.pageSize.height - 8
            );

        }


        const fechaArchivo =
            fecha
                .toISOString()
                .slice(0, 10);


        doc.save(
            `pedidos-kermes-${fechaArchivo}.pdf`
        );

    };


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <>

            <Card>

                <div
                    style={{
                        display: "flex",
                        justifyContent:
                            "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 16
                    }}
                >

                    <span
                        style={{
                            fontSize: 16,
                            fontWeight: 600
                        }}
                    >
                        Pedidos anticipados
                    </span>


                    <Space
                        wrap
                        size={[8, 8]}
                    >

                        <Button
                            icon={
                                <ReloadOutlined />
                            }
                            onClick={reload}
                            loading={loading}
                        >
                            Actualizar
                        </Button>


                        <Button
                            color="green"
                            icon={
                                <FileExcelOutlined />
                            }
                            onClick={exportExcel}
                            disabled={
                                orders.length === 0
                            }
                            variant="outlined"
                        >
                            Excel
                        </Button>


                        <Button
                            danger
                            icon={
                                <FilePdfOutlined />
                            }
                            onClick={exportPDF}
                            disabled={
                                orders.length === 0
                            }
                        >
                            PDF
                        </Button>


                        <Button
                            type="primary"
                            icon={
                                <PlusOutlined />
                            }
                            onClick={() =>
                                setModalOpen(true)
                            }
                        >
                            Nuevo pedido
                        </Button>

                    </Space>

                </div>


                <PreordersTable
                    orders={orders}
                    loading={loading}
                    onChanged={reload}
                    userId={userId}
                    scroll={{ x: 900 }}
                />

            </Card>


            <NewOrderModal
                open={modalOpen}

                onClose={() =>
                    setModalOpen(false)
                }

                onCreated={
                    handleCreated
                }

                userId={userId}
            />

        </>

    );

}